import React, { useState, useEffect, useCallback } from 'react';
import { mergeDeepRight } from 'ramda';

import {
  QueryBuilderConfig,
  QuerySelector,
  QueryBuilderFilters,
  QueryBuilderOnChange,
  QueryBuilderIndicator,
  QueryBuilderDimension,
  QueryBuilderGroupBy,
} from '#coreComponents/QueryBuilder/types';
import { QueryBuilderContext } from '../contexts/QueryBuilderContext';
import { SelectData, SelectOptions } from '#coreComponents/Select/types';
import { useSessionStorage } from '#hooks/useSessionStorage';

export type QueryBuilderContextValues = QueryBuilderOnChange & {
  groupByKey: string;
  indicatorDataKey: string;
};

type SelectorKey = 'querySelectOne' | 'querySelectTwo' | 'querySelectThree';
type Selector =
  | QueryBuilderIndicator
  | QueryBuilderDimension
  | QueryBuilderGroupBy
  | null;
type SessionStorageValues =
  | {
      [keys: string]: QueryBuilderContextValues;
    }
  | undefined;

export const resetSelectedValues = (
  config: QueryBuilderConfig,
): QueryBuilderConfig => {
  const { querySelectOne, querySelectTwo, querySelectThree } = config;
  querySelectOne.meta = { ...querySelectOne.meta, selected: undefined };
  querySelectOne.options = querySelectOne.options.map((option) => {
    if (!option.children) {
      return option;
    }

    return {
      ...option,
      children: mergeDeepRight(option.children, {
        meta: { selected: undefined },
      }),
    };
  });

  if (querySelectTwo) {
    querySelectTwo.meta = { ...querySelectTwo?.meta, selected: undefined };
  }

  if (querySelectThree) {
    querySelectThree.meta = { ...querySelectThree?.meta, selected: undefined };
  }

  return mergeDeepRight(config, {
    querySelectOne,
    querySelectTwo,
    querySelectThree,
  }) as QueryBuilderConfig;
};

export const getSelectorData = (
  config: QueryBuilderConfig,
  selectorKey: SelectorKey,
  selector: Selector,
) => {
  const selectorConfig = config[selectorKey];

  if (!selectorConfig || !selector) {
    return null;
  }

  if (selector.selectedChildren) {
    const updatedOptions = selectorConfig.options.map((option) => {
      if (option.key !== selector.key || !option.children) {
        return option;
      }
      return mergeDeepRight(option, {
        children: {
          meta: { selected: selector.selectedChildren?.map((i) => i.key) },
        },
      }) as SelectOptions;
    });

    // @ts-ignore
    return mergeDeepRight(selectorConfig, {
      options: updatedOptions,
    }) as SelectData;
  }

  return mergeDeepRight(selectorConfig, {
    meta: { selected: selector.key },
  }) as SelectData;
};

export const updateSelectorState = (
  config: QueryBuilderConfig,
  selectorKey: SelectorKey,
  selector: Selector,
) => (prevState: Maybe<QuerySelector>) => {
  if (!selector) {
    return null;
  }
  const data = getSelectorData(config, selectorKey, selector);

  if (data) {
    return {
      label: selector.label,
      data,
      selectedItems: selector.selectedChildren?.map((i) => i.key) || [
        selector.key,
      ],
    };
  }

  return prevState;
};

export const createQueryFromConfig = (
  config: QueryBuilderConfig,
  sessionStorageValue?: QueryBuilderContextValues,
): QueryBuilderContextValues => {
  const indicatorData = config.querySelectOne.options.find((item) => {
    const selectedKey =
      sessionStorageValue?.indicatorDataKey ||
      config.querySelectOne.meta?.selected;
    return selectedKey
      ? item.key === selectedKey
      : item.key ===
          config.querySelectOne.options.find(
            (option) => option.children?.meta?.selected,
          )?.key;
  })!;

  const mapItem = config.map.find((i) => i.key === indicatorData.key)!;
  const selectedChildren = indicatorData.children
    ? indicatorData.children.options.filter(({ key }) =>
        indicatorData.children?.meta?.selected?.includes(key),
      )
    : undefined;

  const indicator = {
    key: mapItem.key,
    type: mapItem.type,
    options: mapItem.options,
    querySelectTwo: mapItem.querySelectTwo,
    querySelectThree: mapItem.querySelectThree,
    filteredOptions: mapItem.filteredOptions,
    gql: mapItem.gql,
    label: indicatorData.label,
    selectedChildren,
    selectedFilters: Object.entries(config.filteredOptions)
      .filter(([, values]) => values.meta?.selected)
      .reduce(
        (acc, [key, values]) => ({ ...acc, [key]: values.meta?.selected }),
        {},
      ),
  };

  const dimension = config.variableOptions.find(({ key }) => {
    const { querySelectTwo: selectTwo, options } = mapItem;
    return selectTwo ? key === selectTwo : key === options![0];
  })!;

  const { label: groupByLabel, key: groupByKey } = config.variableOptions.find(
    ({ key }) => {
      const { querySelectThree: selectThree, options } = mapItem;
      return selectThree ? key === selectThree : key === options![1];
    },
  )!;
  const groupBy = {
    label: groupByLabel,
    key: groupByKey,
    dataOptions: config.dataOptions[groupByKey],
  };

  return {
    groupByKey: sessionStorageValue?.groupByKey || groupByKey,
    indicatorDataKey:
      sessionStorageValue?.indicatorDataKey || indicatorData.key,
    indicator: sessionStorageValue?.indicator
      ? {
          ...sessionStorageValue.indicator,
          gql: mapItem.gql,
          querySelectTwo: mapItem.querySelectTwo,
          querySelectThree: mapItem.querySelectThree,
        }
      : indicator,
    dimension: sessionStorageValue?.dimension || dimension,
    groupBy: sessionStorageValue?.groupBy
      ? {
          ...sessionStorageValue.groupBy,
          dataOptions:
            config.dataOptions[sessionStorageValue?.groupByKey || groupByKey],
        }
      : groupBy,
    years: sessionStorageValue?.years.length
      ? sessionStorageValue.years
      : config.defaultYears ?? config.years.slice(-2),
    filters: sessionStorageValue?.filters || indicator.selectedFilters,
  };
};
const QueryBuilderProvider: React.FC<{ config?: QueryBuilderConfig }> = ({
  config: initialConfig,
  children,
}) => {
  const [config, setConfig] = useState<Maybe<QueryBuilderConfig>>(
    initialConfig || null,
  );
  const [query, setQuery] = useState<Maybe<QueryBuilderOnChange>>(null);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<QueryBuilderFilters>(
    {},
  );
  const [querySelectOne, setQuerySelectOne] = useState<Maybe<QuerySelector>>(
    null,
  );
  const [querySelectTwo, setQuerySelectTwo] = useState<Maybe<QuerySelector>>(
    null,
  );
  const [querySelectThree, setQuerySelectThree] = useState<
    Maybe<QuerySelector>
  >(null);

  const [sessionStorageValues, setSessionStorageValues] = useSessionStorage<
    SessionStorageValues
  >('queryBuilderValue', undefined);
  const [pageName, setPageName] = useState<Maybe<string>>(null);

  const setPageConfig = useCallback(
    (newConfig: QueryBuilderConfig, newPageName: string) => {
      setConfig(newConfig);
      setPageName(newPageName);
    },
    [],
  );
  const applyChanges = useCallback(
    (args: QueryBuilderContextValues) => {
      if (!config) {
        return;
      }
      const {
        indicator,
        dimension,
        groupBy,
        years,
        filters,
        groupByKey,
        indicatorDataKey,
      } = args;
      const cleanedConfig = resetSelectedValues({ ...config });
      setQuery(args);
      if (pageName) {
        setSessionStorageValues((pre) => ({
          ...pre,
          [pageName]: {
            dimension,
            indicator,
            groupBy,
            filters,
            years,
            groupByKey,
            indicatorDataKey,
          },
        }));
      }
      setQuerySelectOne(
        updateSelectorState(cleanedConfig, 'querySelectOne', indicator),
      );
      setQuerySelectTwo(
        updateSelectorState(cleanedConfig, 'querySelectTwo', dimension),
      );
      setQuerySelectThree(
        updateSelectorState(cleanedConfig, 'querySelectThree', groupBy),
      );
      setSelectedYears(years);
      setSelectedFilters(filters);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config],
  );

  useEffect(() => {
    if (config && pageName) {
      applyChanges(
        createQueryFromConfig(
          config,
          sessionStorageValues && sessionStorageValues[pageName],
        ),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, applyChanges]);

  return (
    <QueryBuilderContext.Provider
      value={{
        config,
        query,
        filters: {
          selectedYears,
          selectedFilters,
          querySelectOne,
          querySelectTwo,
          querySelectThree,
        },
        setConfig: setPageConfig,
        applyChanges,
      }}
    >
      {children}
    </QueryBuilderContext.Provider>
  );
};

export default QueryBuilderProvider;
